import { useEffect, useMemo, useRef } from 'react';
import { getDefaultPercentChartOptions } from '../config/percentChartConfig';

/**
 * Owns one percent chart's options and its forward-estimate x-axis band.
 *
 * The first time the chart mounts it receives the full Highcharts options,
 * including the range selector. Later updates pass only `series`, the same
 * way the ratio chart's value-basis / display-scale rebuilds avoid
 * `chart.update({ rangeSelector })`, which would snap the zoom back to the
 * default window. Hiding the chart (empty state) arms a full options object
 * again so the next mount still has its range presets.
 */
const useAnnualPercentChart = (
  series, chartActive, yAxisTitle = 'Percent', estimatedBand = null, defaultRange = null,
) => {
  const chartRef = useRef(null);
  const needsFull = useRef(true);
  // Which `defaultRange` has been applied to which chart instance.
  const appliedRange = useRef({ chart: null, range: null });
  if (!chartActive) needsFull.current = true;

  const chartOptions = useMemo(() => {
    // `chartActive` is a real dependency: hiding the chart arms a full option
    // set (range selector included) so the next mount is not stuck with a
    // series-only update object.
    if (!chartActive || needsFull.current) {
      return { ...getDefaultPercentChartOptions(yAxisTitle), series };
    }
    return { series };
  }, [series, chartActive, yAxisTitle]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) {
      needsFull.current = true;
      return;
    }
    needsFull.current = false;
    // A series-only update carries no xAxis, so a band declared in the
    // options object would survive a symbol switch and sit grey over
    // another company's reported years.
    const xAxis = chart.xAxis && chart.xAxis[0];
    if (xAxis) {
      xAxis.removePlotBand('estimated-region');
      (estimatedBand || []).forEach((band) => xAxis.addPlotBand(band));
    }
    const axis = chart.yAxis && chart.yAxis[0];
    if (!axis) return;
    // Series-only updates (above) carry no yAxis, so the title is set here —
    // a mode switch must move it.
    axis.setTitle({ text: yAxisTitle });
  }, [chartOptions, chartActive, yAxisTitle, estimatedBand]);

  // Open on the same window the ratio chart gets (useStockData's
  // computeDefaultRange) instead of the 15y button's own window, which
  // counts back from this chart's dataMax — a forward estimate point.
  // Applied once per chart instance and once per fetch (fetchData sets a new
  // `defaultRange` object every time), so a later range-button click or zoom
  // is never snapped back by a mode or selection change.
  useEffect(() => {
    const chart = chartRef.current?.chart;
    const xAxis = chart?.xAxis && chart.xAxis[0];
    if (!xAxis || !defaultRange) return;
    const applied = appliedRange.current;
    if (applied.chart === chart && applied.range === defaultRange) return;
    appliedRange.current = { chart, range: defaultRange };
    xAxis.setExtremes(defaultRange.min, defaultRange.max);
  }, [chartOptions, chartActive, defaultRange]);

  return { chartRef, chartOptions };
};

export default useAnnualPercentChart;
